import type { ChartTheme } from '../theme';

import { joinSeriesToDays } from './buildChartData';
import {
    buildAnnotations,
    buildBarLabelOptions,
    buildLegendOptions,
    buildZoomOptions,
} from './chartPluginOptions';
import { axisScaleId } from './constants';
import type { MetricChartOptions, MetricChartOptionsByKind } from './optionTypes';
import { buildTooltipOptions } from './tooltipOptions';
import type { MetricChartKind, MetricChartSpec } from './types';
import { buildAxisScale } from './valueAxisScale';
import { buildXAxisScale } from './xAxisScale';

/** Every value of the series plotted on `axisKey`, for an axis that fits its range to them. */
function valuesOnAxis(spec: MetricChartSpec<MetricChartKind>, axisKey: string): number[] {
    return spec.series
        .filter((series) => (series.axis ?? 'left') === axisKey)
        .flatMap((series) => joinSeriesToDays(series, spec.days))
        .filter((value): value is number => value !== null && Number.isFinite(value));
}

/**
 * Builds Chart.js options from a spec: both value axes, the localised day axis,
 * tooltips that resolve each series' own axis unit, and pan/zoom on x.
 *
 * The Chart.js legend is disabled on purpose — legends are rendered as React
 * nodes by `DatasetLegend` so they can carry checkboxes and tooltips.
 *
 * Every colour the options carry comes from `theme`, rather than from the Chart.js defaults,
 * so a chart under a `ChartThemeOverride` draws in that theme whatever the page uses.
 */
export function buildMetricChartOptions<TKind extends MetricChartKind>(
    spec: MetricChartSpec<TKind>,
    theme: ChartTheme
): MetricChartOptions<TKind> {
    const { locale, left, right, series, xAxisKind = 'day', animationDuration, extraAxes } = spec;
    const stacked = (left.stacked ?? false) || (right?.stacked ?? false);

    const options = {
        responsive: true,
        ...(spec.plotPadding ? { layout: { padding: spec.plotPadding } } : {}),
        // Height is controlled by the card's container, not by an aspect ratio.
        maintainAspectRatio: false,
        ...(animationDuration === undefined ? {} : { animation: { duration: animationDuration } }),
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        scales: {
            // `stacked` on a value axis alone is not enough in Chart.js — the
            // category axis has to agree, so it is threaded into the x axis too.
            x: buildXAxisScale({
                locale,
                stacked,
                xAxisKind,
                theme,
                categoryLabel: spec.categoryLabel,
                xTicks: spec.xTicks,
                days: spec.days,
                gridLines: spec.gridLines,
            }),
            yAxisLeft: buildAxisScale(
                left,
                'left',
                locale,
                theme,
                valuesOnAxis(spec, 'left'),
                spec.gridLines
            ),
            ...(right && (!right.hideWhenUnused || series.some((entry) => entry.axis === 'right'))
                ? {
                      yAxisRight: buildAxisScale(
                          right,
                          'right',
                          locale,
                          theme,
                          valuesOnAxis(spec, 'right'),
                          spec.gridLines
                      ),
                  }
                : {}),
            // Only the extra axes a visible series plots on. The hook filters hidden
            // series out of the spec before this runs, so toggling an overlay off in
            // the legend takes its axis with it rather than leaving an empty scale.
            ...Object.fromEntries(
                Object.entries(extraAxes ?? {})
                    .filter(([key]) => series.some((entry) => entry.axis === key))
                    .map(([key, axis]) => [
                        axisScaleId(key),
                        buildAxisScale(
                            axis,
                            'right',
                            locale,
                            theme,
                            valuesOnAxis(spec, key),
                            spec.gridLines
                        ),
                    ])
            ),
        },
        plugins: {
            legend: buildLegendOptions(spec, theme),
            ...buildAnnotations(spec, theme),
            ...buildBarLabelOptions(spec, theme),
            ...(stacked
                ? {
                      // Match legacy OptoMixedChart for stacked area charts only.
                      filler: {
                          propagate: false,
                      },
                  }
                : {}),
            tooltip: buildTooltipOptions(spec, theme, stacked),
            ...buildZoomOptions(spec, theme),
            // Names the first column of the chart menu's CSV download, and its keys.
            csvExport: {
                xAxisKind: spec.xAxisKind ?? 'day',
                ...(spec.xAxisKind === 'category' && spec.categoryLabel
                    ? { xLabels: spec.days.map(spec.categoryLabel) }
                    : {}),
            },
        },
    };

    // As in `buildMetricChartData`: the options are valid for both 'line' and 'bar', so
    // they are checked against each kind and looked up by `spec.kind`.
    const byKind: MetricChartOptionsByKind = { line: options, bar: options };
    return byKind[spec.kind];
}
